// StudentDetail.js (Enhanced Version)
import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Avatar,
  Chip,
  Divider,
  IconButton,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Card,
  CardContent,
  alpha,
  useTheme,
  CircularProgress,
  Modal,
  Fade,
  Backdrop,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Stepper,
  Step,
  StepLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  School as SchoolIcon,
  Class as ClassIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Book as BookIcon,
  Male as MaleIcon,
  Female as FemaleIcon,
  LocalHospital as MedicalIcon,
  Warning as WarningIcon,
  Contacts as ContactsIcon,
  Description as DescriptionIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Image as ImageIcon,
  InsertDriveFile as InsertDriveFileIcon,
  Download as DownloadIcon,
  ZoomIn as ZoomInIcon,
  Close as CloseIcon,
  FamilyRestroom as FamilyRestroomIcon,
  Work as WorkIcon,
  MedicalServices as MedicalServicesIcon,
  Vaccines as VaccinesIcon,
  HistoryEdu as HistoryEduIcon,
  Bloodtype as BloodtypeIcon,
  Language as LanguageIcon,
  Flag as FlagIcon,
  Translate as TranslateIcon,
  Home as HomeIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import AddStudentForm from "./AddStudent"; // Your AddStudentForm component
import apiEndpoints from "../../apiconfig";
import mapStudentForForm from "./mapStudentForForm";
import { useSnackbar } from "../Snackbar/SnackbarContext";

const StudentDetail = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id: guid } = useParams();
  const { showSnackbar } = useSnackbar();
  const [student, setStudent] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [documents, setDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [checklistDocuments, setChecklistDocuments] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [fullStudentData, setFullStudentData] = useState(null);

  useEffect(() => {
    fetchStudent();
  }, [guid]);

  const fetchStudent = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      const res = await fetch(`${apiEndpoints.student}?guid=${guid}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();

      if (data.status === "success") {
        const s = data.data;
        // Same mapping as the Student list, so the edit form is pre-filled correctly
        setFullStudentData(mapStudentForForm(s));

        // Set basic student info
        setStudent({
          guid: s.guid,
          firstName: s.first_name,
          lastName: s.last_name,
          photo: s.profile_photo,
          gender: s.gender,
          dob: s.date_of_birth,
          admissionNumber: s.admission_number,
          classGuid: s.class_guid,
          class: s.class_name,
          parentName: `${s.parent_first} ${s.parent_last}`,
          parentPhone: s.parent_mobile,
          parentEmail: s.parent_email || s.parentEmail || s.email || "",
          username: (() => {
            if (s.admission_number) {
              const parts = s.admission_number.split("-");
              if (parts.length >= 2) {
                const seq = parseInt(parts[1], 10);
                if (!isNaN(seq)) {
                  return "RM" + String(seq).padStart(7, "0");
                }
              }
            }
            return s.username || "";
          })(),
          parent_photo: s.parent_photo || "",
          parentAddress: s.parent_address || "",
          studentBranch: s.student_branch || "",
          joiningDate: s.joining_date,
          bloodGroup: s.blood_group,
          status: s.isActive ? "Active" : "Inactive",
          address: s.address || "",
          previousSchool: s.previous_school || "",
          medicalInfo: s.medical_info || "",
          emergencyContact:
            s.emergency_contact || `${s.parent_first} (${s.parent_mobile})`,

          // Additional fields from AddStudentForm
          religion: s.religion || "",
          nationality: s.nationality || "",
          motherTongue: s.mother_tongue || "",
          placeOfBirth: s.place_of_birth || "",
          placeOfOrigin: s.place_of_origin || "",

          // Mother details
          motherName: s.mother_first_name || "",
          motherQualification: s.mother_qualification || "",
          motherOccupation: s.mother_occupation || "",
          motherWorkPlace:
            s.mother_work_place ||
            s.mother_placeofwork ||
            s.mother_place_of_work ||
            "",
          motherOfficialAddress:
            s.mother_official_address || s.mother_address || "",
          motherEmail: s.mother_email || "",
          motherMobile: s.mother_mobile || "",

          // Medical details
          allergies: s.allergies || "",
          surgeries: s.surgeries || "",
          chronicIllness: s.chronic_illness || "",
          immunization: s.immunization || "",

          // Emergency contact details
          emergencyContactName: s.emergency_contact_name || "",
          emergencyContactPhone: s.emergency_contact_phone || "",
          emergencyRelationship: s.emergency_relationship || "",

          // Guardian details
          guardianName: s.guardian_name || "",
          guardianRelationship: s.guardian_relationship || "",
          guardianPhone: s.guardian_phone || "",
          guardianMobile: s.guardian_mobile || "",

          // Previous school
          // API field names: attended_school_before ("Yes"/"No"), previous_school,
          // previous_school_duration, previous_class_attended
          previouslyAttendedSchool:
            String(s.attended_school_before || "").toLowerCase() === "yes" ? "yes" : "no",
          previousSchoolName: s.previous_school || "",
          previousSchoolDuration: s.previous_school_duration || "",
          previousSchoolClass: s.previous_class_attended || "",

          // Siblings
          siblings: s.siblings || [],

          // Declaration
          declarationParentName: s.declaration_parent_name || "",
          declarationDate: s.declaration_date || "",
          declarationSignature: s.declaration_signed_by || "",

          // Checklist documents
          checklist_documents: s.checklist_documents,
          birthCertificate: s.birth_certificate_submitted === "Yes" || false,
          immunizationRecord:
            s.immunization_record_submitted === "Yes" || false,
          transferCertificate:
            s.transfer_certificate_submitted === "Yes" || false,
          progressReport: s.progress_report_submitted === "Yes" || false,
          passportPhotos: s.passport_photos_submitted === "Yes" || false,
          passportCopy: s.passport_copy_submitted === "Yes" || false,
          medicalReport: s.medical_report_submitted === "Yes" || false,
          otherDocuments: s.other_documents_submitted === "Yes" || false,
        });

        // Parse checklist documents
        if (s.checklist_documents) {
          try {
            const checklistData =
              typeof s.checklist_documents === "string"
                ? JSON.parse(s.checklist_documents)
                : s.checklist_documents;
            setChecklistDocuments(checklistData);
          } catch (error) {
            console.error("Error parsing checklist documents:", error);
          }
        }

        // Collect all documents
        fetchAllDocuments(s);
      } else {
        setError(data.message || "Failed to fetch student details");
      }
    } catch (err) {
      console.error("Failed to fetch student details", err);
      setError("Error loading student details");
    } finally {
      setLoading(false);
    }
  };

  const detectFileType = (rawFile, fileName = "") => {
    if (!rawFile) return { isImage: false, isPdf: false, cleanUrl: "" };

    let fileStr = rawFile;
    if (typeof rawFile === "object" && rawFile !== null) {
      fileStr =
        rawFile.data ||
        rawFile.file ||
        rawFile.file_data ||
        rawFile.url ||
        rawFile.path ||
        rawFile.file_path ||
        "";
    }

    if (typeof fileStr !== "string") {
      return { isImage: false, isPdf: false, cleanUrl: "" };
    }

    fileStr = fileStr.trim();
    if (!fileStr) return { isImage: false, isPdf: false, cleanUrl: "" };

    const name = String(fileName || "").toLowerCase();

    // 1️⃣ Check Content Magic Bytes FIRST
    let isImage = false;
    let isPdf = false;
    let mimeType = "";

    if (fileStr.startsWith("data:")) {
      const mimeMatch = fileStr.match(/^data:([^;]+);/);
      if (mimeMatch) {
        mimeType = mimeMatch[1].toLowerCase();
        if (mimeType.startsWith("image/")) isImage = true;
        if (mimeType.includes("pdf")) isPdf = true;
      }
    }

    // Check base64 magic bytes
    if (!isImage && !isPdf) {
      const cleanBase64 = fileStr.replace(/^data:[^;]+;base64,/, "").trim();
      if (
        cleanBase64.startsWith("/9j/") || // JPEG
        cleanBase64.startsWith("iVBORw") || // PNG
        cleanBase64.startsWith("R0lGOD") || // GIF
        cleanBase64.startsWith("UklGR") // WEBP
      ) {
        isImage = true;
        mimeType = "image/png";
      } else if (cleanBase64.startsWith("JVBER")) {
        // PDF (%PDF)
        isPdf = true;
        mimeType = "application/pdf";
      }
    }

    // 2️⃣ Fallback to Filename Extension if magic bytes inconclusive
    if (!isImage && !isPdf) {
      if (
        [".jpg", ".jpeg", ".png", ".gif", ".webp", ".bmp", ".svg"].some((ext) =>
          name.endsWith(ext),
        )
      ) {
        isImage = true;
        mimeType = "image/png";
      } else if (name.endsWith(".pdf")) {
        isPdf = true;
        mimeType = "application/pdf";
      }
    }

    // 3️⃣ Construct Clean URL / Data URI
    let cleanUrl = fileStr;
    if (
      !fileStr.startsWith("http://") &&
      !fileStr.startsWith("https://") &&
      !fileStr.startsWith("data:")
    ) {
      if (fileStr.startsWith("/uploads/") || fileStr.startsWith("uploads/")) {
        const cleanPath = fileStr.startsWith("/") ? fileStr : `/${fileStr}`;
        cleanUrl = `https://admin.rootsmontessorischool.in${cleanPath}`;
      } else {
        const defaultMime = isImage
          ? "image/png"
          : isPdf
            ? "application/pdf"
            : "application/octet-stream";
        cleanUrl = `data:${mimeType || defaultMime};base64,${fileStr.replace(/\s/g, "")}`;
      }
    }

    return { isImage, isPdf, cleanUrl };
  };

  const fetchAllDocuments = (studentData) => {
    setLoadingDocuments(true);
    const documentsList = [];

    try {
      // Add profile photo if exists
      if (studentData.profile_photo) {
        documentsList.push({
          type: "Profile Photo",
          file: studentData.profile_photo,
          fileName: "profile_photo.jpg",
          isImage: true,
          source: "profile",
        });
      }

      if (studentData.parent_photo) {
        documentsList.push({
          type: "Parent Photo",
          file: studentData.parent_photo,
          fileName: "parent_photo.jpg",
          isImage: true,
          source: "profile",
        });
      }

      // Add checklist documents
      if (studentData.checklist_documents) {
        try {
          const checklistData =
            typeof studentData.checklist_documents === "string"
              ? JSON.parse(studentData.checklist_documents)
              : studentData.checklist_documents;

          if (checklistData && typeof checklistData === "object") {
            Object.entries(checklistData).forEach(([key, doc]) => {
              if (doc && (doc.file || doc.data)) {
                const fileData = doc.file || doc.data;
                const fileName = doc.fileName || doc.name || `${key}.pdf`;
                const { isImage } = detectFileType(fileData, fileName);
                documentsList.push({
                  type: doc.type || key.replace(/([A-Z])/g, " $1").trim(),
                  file: fileData,
                  fileName: fileName,
                  isImage: isImage,
                  source: "checklist",
                });
              }
            });
          }
        } catch (error) {
          console.error("Error parsing checklist documents:", error);
        }
      }

      // Add document_files object if present
      if (
        studentData.document_files &&
        typeof studentData.document_files === "object"
      ) {
        Object.entries(studentData.document_files).forEach(([key, docObj]) => {
          if (docObj && docObj.data) {
            const fileName = docObj.name || `${key}.pdf`;
            const { isImage } = detectFileType(docObj.data, fileName);
            documentsList.push({
              type: key.replace("File", "").replace(/([A-Z])/g, " $1").trim(),
              file: docObj.data,
              fileName: fileName,
              isImage: isImage,
              source: "document_files",
            });
          }
        });
      }

      // Add existing documents array from API
      if (studentData.documents && Array.isArray(studentData.documents)) {
        studentData.documents.forEach((doc, index) => {
          if (doc.file_data) {
            const fileName = doc.file_name || `document_${index + 1}`;
            const { isImage } = detectFileType(doc.file_data, fileName);
            documentsList.push({
              type: doc.document_type || "Document",
              file: doc.file_data,
              fileName: fileName,
              isImage: isImage,
              source: "api",
            });
          }
        });
      }

      // Add individual file fields
      const fileFields = [
        { field: "birth_certificate_file", type: "Birth Certificate" },
        { field: "immunization_record_file", type: "Immunization Record" },
        { field: "transfer_certificate_file", type: "Transfer Certificate" },
        { field: "progress_report_file", type: "Progress Report" },
        { field: "passport_copy_file", type: "Passport Copy" },
        { field: "medical_report_file", type: "Medical Report" },
        { field: "passport_photos_file", type: "Passport Photos" },
        { field: "other_documents_file", type: "Other Documents" },
      ];

      fileFields.forEach(({ field, type }) => {
        const fileData = studentData[field];
        if (fileData) {
          const { isImage } = detectFileType(fileData, type);
          documentsList.push({
            type,
            file: fileData,
            fileName:
              type.toLowerCase().replace(/\s+/g, "_") +
              (isImage ? ".jpg" : ".pdf"),
            isImage: isImage,
            source: "file_field",
          });
        }
      });

      // Deduplicate documentsList by normalized document category/type
      const docMap = new Map();

      documentsList.forEach((doc) => {
        if (!doc.file) return;

        // Clean document type (e.g. "Immunization Record" -> "immunizationrecord")
        const typeKey = (doc.type || "")
          .toLowerCase()
          .replace(/[^a-z0-9]/g, "");

        // Clean file data fingerprint
        const fileStr = String(doc.file).trim();
        const dataFingerprint =
          fileStr.length > 150 ? fileStr.substring(0, 150) : fileStr;

        const key =
          typeKey && typeKey !== "otherdocuments" && typeKey !== "document"
            ? typeKey
            : `${typeKey}_${dataFingerprint}`;

        if (!docMap.has(key)) {
          docMap.set(key, doc);
        } else {
          // If already existing, prefer entry with real human-readable original filename
          const existing = docMap.get(key);
          const currentHasBetterName =
            doc.fileName && !/^[a-f0-9]{10,}/.test(doc.fileName);
          const existingHasHashedName =
            existing.fileName && /^[a-f0-9]{10,}/.test(existing.fileName);

          if (currentHasBetterName && existingHasHashedName) {
            docMap.set(key, doc);
          }
        }
      });

      const deduplicatedList = Array.from(docMap.values());
      setDocuments(deduplicatedList);
    } catch (error) {
      console.error("Error collecting documents:", error);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const getFileIcon = (fileName) => {
    if (!fileName) return <InsertDriveFileIcon />;

    const ext = fileName.split(".").pop().toLowerCase();
    if (["pdf"].includes(ext)) return <PictureAsPdfIcon color="error" />;
    if (["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(ext))
      return <ImageIcon color="primary" />;
    return <InsertDriveFileIcon />;
  };

  const getFileType = (fileName) => {
    if (!fileName) return "File";

    const ext = fileName.split(".").pop().toLowerCase();
    const typeMap = {
      pdf: "PDF Document",
      zip: "ZIP Archive",
      jpg: "JPEG Image",
      jpeg: "JPEG Image",
      png: "PNG Image",
      gif: "GIF Image",
      doc: "Word Document",
      docx: "Word Document",
      xls: "Excel Spreadsheet",
      xlsx: "Excel Spreadsheet",
      txt: "Text File",
      csv: "CSV File",
    };

    return typeMap[ext] || `${ext.toUpperCase()} File`;
  };

  const handleDownloadFile = (rawFile, fileName) => {
    const { cleanUrl } = detectFileType(rawFile, fileName);
    if (!cleanUrl) {
      showSnackbar("No file data available", "warning");
      return;
    }

    try {
      const link = document.createElement("a");
      link.href = cleanUrl;
      link.download = fileName || "document";
      if (!fileName) {
        const timestamp = new Date().getTime();
        link.download = `document_${timestamp}`;
      }

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading file:", error);
      showSnackbar(`Error downloading file: ${error.message || "Invalid file data"}`, "error");
    }
  };

  const handleViewFile = (rawFile, fileName, fileType) => {
    const { isImage, isPdf, cleanUrl } = detectFileType(rawFile, fileName);
    if (!cleanUrl) {
      showSnackbar("No valid file data available to preview", "warning");
      return;
    }

    setSelectedFile({
      data: cleanUrl,
      name: fileName || fileType || "Document",
      type: fileType || "Document",
      isImage: isImage,
      isPdf: isPdf,
    });
  };

  const handleCloseFileModal = () => {
    setSelectedFile(null);
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
  };

  useEffect(() => {
    if (!selectedFile || selectedFile.isImage) {
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
        setPdfBlobUrl(null);
      }
      return;
    }

    const { cleanUrl } = detectFileType(selectedFile.data, selectedFile.name);
    if (!cleanUrl) return;

    if (cleanUrl.startsWith("http://") || cleanUrl.startsWith("https://")) {
      setPdfBlobUrl(cleanUrl);
      return;
    }

    try {
      let base64Content = cleanUrl;
      let mimeType = "application/pdf";
      if (cleanUrl.startsWith("data:")) {
        const mimeMatch = cleanUrl.match(/^data:([^;]+);/);
        if (mimeMatch) mimeType = mimeMatch[1];
        const commaIndex = cleanUrl.indexOf(",");
        if (commaIndex !== -1) base64Content = cleanUrl.substring(commaIndex + 1);
      }
      base64Content = base64Content.replace(/\s/g, "");
      const byteCharacters = atob(base64Content);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });
      const createdUrl = URL.createObjectURL(blob);
      setPdfBlobUrl(createdUrl);
    } catch (e) {
      console.error("Error creating Blob URL for PDF preview:", e);
      setPdfBlobUrl(cleanUrl);
    }
  }, [selectedFile]);

  const handleEdit = () => {
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
  };

  const handleEditSuccess = () => {
    setEditDialogOpen(false);
    setSuccessMessage("Student updated successfully!");
    fetchStudent(); // Refresh data
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const handleDelete = () => {
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${apiEndpoints.student}?guid=${guid}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.status === "success") {
        setSuccessMessage("Student deleted successfully!");
        setTimeout(() => {
          navigate("/admin/student-listing");
        }, 1500);
      } else {
        setError(data.message || "Failed to delete student");
      }
    } catch (err) {
      console.error("Error deleting student:", err);
      setError("Error deleting student");
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not specified";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const calculateAge = (dob) => {
    if (!dob) return "N/A";
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return age;
  };

  const GenderIcon = ({ gender }) => {
    const Icon = gender === "Male" ? MaleIcon : FemaleIcon;
    const color = gender === "Male" ? "info.main" : "#fe0596ff";
    return <Icon sx={{ color, fontSize: 30 }} />;
  };

  const renderDocumentsGrid = () => {
    if (loadingDocuments) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (documents.length === 0) {
      return (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <DescriptionIcon
            sx={{ fontSize: 60, color: "text.disabled", mb: 2 }}
          />
          <Typography variant="h6" color="text.secondary">
            No documents found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            No documents have been uploaded for this student.
          </Typography>
        </Box>
      );
    }

    return (
      <Box>
        <Typography variant="subtitle1" sx={{ mb: 3, color: "text.secondary" }}>
          Total Documents: {documents.length}
        </Typography>
        <Grid container spacing={3}>
          {documents.map((doc, index) => {
            const { isImage, cleanUrl } = detectFileType(doc.file, doc.fileName);
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    cursor: "pointer",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: 6,
                    },
                  }}
                  onClick={() =>
                    handleViewFile(doc.file, doc.fileName, doc.type)
                  }
                >
                  <Box sx={{ position: "relative", pt: "75%" }}>
                    <CardContent
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        p: 0,
                      }}
                    >
                      {isImage ? (
                        <Box
                          sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "background.default",
                            backgroundImage: `url(${cleanUrl})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            backgroundRepeat: "no-repeat",
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "background.default",
                            p: 2,
                          }}
                        >
                          {getFileIcon(doc.fileName)}
                          <Typography
                            variant="caption"
                            sx={{ mt: 1, textAlign: "center", fontWeight: 600 }}
                          >
                            {getFileType(doc.fileName)}
                          </Typography>
                        </Box>
                      )}
                    </CardContent>

                    {/* Preview / Zoom Button */}
                    <IconButton
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 8,
                        left: 8,
                        bgcolor: "rgba(255,255,255,0.9)",
                        boxShadow: 1,
                        "&:hover": {
                          bgcolor: "#ffffff",
                        },
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewFile(doc.file, doc.fileName, doc.type);
                      }}
                      title="Preview Document"
                    >
                      <ZoomInIcon fontSize="small" />
                    </IconButton>

                    {/* Download Button */}
                    <IconButton
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        bgcolor: "rgba(255,255,255,0.9)",
                        boxShadow: 1,
                        "&:hover": {
                          bgcolor: "#ffffff",
                        },
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadFile(doc.file, doc.fileName);
                      }}
                      title="Download Document"
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <CardContent sx={{ flexGrow: 1, p: 2 }}>
                    <Typography
                      variant="subtitle2"
                      noWrap
                      sx={{ fontWeight: 600 }}
                    >
                      {doc.type}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      noWrap
                      display="block"
                    >
                      {doc.fileName}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  };

  const renderFileModal = () => {
    if (!selectedFile) return null;

    const isImg = selectedFile.isImage;

    return (
      <Modal
        open={Boolean(selectedFile)}
        onClose={handleCloseFileModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={Boolean(selectedFile)}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "90vw",
              height: "90vh",
              bgcolor: "#0f172a",
              boxShadow: 24,
              borderRadius: "16px",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header Bar */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 3,
                py: 2,
                bgcolor: "rgba(255, 255, 255, 0.05)",
                borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <DescriptionIcon sx={{ color: "primary.main" }} />
                <Typography variant="h6" color="white" fontWeight="600">
                  {selectedFile.type || selectedFile.name}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() =>
                    window.open(pdfBlobUrl || selectedFile.data, "_blank")
                  }
                  sx={{ color: "white", borderColor: "rgba(255,255,255,0.3)" }}
                >
                  Open in New Tab
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={() =>
                    handleDownloadFile(selectedFile.data, selectedFile.name)
                  }
                  sx={{ color: "white", borderColor: "rgba(255,255,255,0.3)" }}
                >
                  Download
                </Button>
                <IconButton
                  onClick={handleCloseFileModal}
                  sx={{ color: "white" }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>

            {/* Document Content View */}
            <Box
              sx={{
                flex: 1,
                width: "100%",
                height: "calc(100% - 64px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 2,
                overflow: "hidden",
              }}
            >
              {isImg ? (
                <img
                  src={selectedFile.data}
                  alt={selectedFile.name}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                    borderRadius: "8px",
                  }}
                />
              ) : (
                <iframe
                  src={pdfBlobUrl || selectedFile.data}
                  title={selectedFile.name}
                  width="100%"
                  height="100%"
                  style={{
                    border: "none",
                    borderRadius: "8px",
                    backgroundColor: "#ffffff",
                  }}
                />
              )}
            </Box>
          </Box>
        </Fade>
      </Modal>
    );
  };

  const renderSiblingsTable = () => {
    if (!student.siblings || student.siblings.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          No sibling information available.
        </Typography>
      );
    }

    return (
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Gender</TableCell>
              <TableCell>Age</TableCell>
              <TableCell>Class</TableCell>
              <TableCell>School</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {student.siblings.map((sibling, index) => (
              <TableRow key={index}>
                <TableCell>{sibling.name}</TableCell>
                <TableCell>{sibling.gender}</TableCell>
                <TableCell>{sibling.age || "-"}</TableCell>
                <TableCell>{sibling.class}</TableCell>
                <TableCell>{sibling.school}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  if (loading) {
    return (
      <Container sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !student) {
    return (
      <Container>
        <Box sx={{ textAlign: "center", py: 10 }}>
          <Typography variant="h6" color="error" gutterBottom>
            {error || "Student not found"}
          </Typography>
          <Button
            onClick={() => navigate("/admin/student-listing")}
            startIcon={<ArrowBackIcon />}
            sx={{ mt: 2 }}
          >
            Back to Student Directory
          </Button>
        </Box>
      </Container>
    );
  }

  const handleBack = () => {
    navigate("/admin/student-listing");
  };

  return (
    <Container
      maxWidth="xl"
      sx={{ py: 4, fontFamily: "Montserrat, sans-serif" }}
    >
      {/* Success/Error Messages */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="success">{successMessage}</Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={3000}
        onClose={() => setError("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="error">{error}</Alert>
      </Snackbar>

      {/* Header with Action Buttons */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack}>
          Back to Student Directory
        </Button>
      </Box>

      {/* Header Section */}
      <Paper
        sx={{
          p: 4,
          mb: 4,
          borderRadius: "16px",
          background: `linear-gradient(135deg, 
          ${alpha(theme.palette.primary.main, 0.05)} 0%, 
          ${alpha(theme.palette.secondary.main, 0.05)} 100%
        )`,
        }}
      >
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={3}>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Avatar
                src={
                  student.photo && student.photo !== ""
                    ? student.photo.startsWith("http") || student.photo.startsWith("data:")
                      ? student.photo
                      : `data:image/jpeg;base64,${student.photo}`
                    : undefined
                }
                sx={{
                  width: 200,
                  height: 200,
                  border: `6px solid ${theme.palette.background.paper}`,
                  boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
                }}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={9}>
            <Grid container spacing={2} alignItems="flex-start" mb={2}>
              <Grid item xs={12} md={9}>
                <Typography variant="h3" fontWeight="700" gutterBottom sx={{textTransform: "uppercase"}}>
                  {student.firstName} {student.lastName}
                </Typography>

                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}
                >
                  <GenderIcon gender={student.gender} />
                  <Typography variant="h5" color="primary" fontWeight="600">
                    {student.class}
                  </Typography>
                </Box>

                <Typography variant="h6" color="text.secondary">
                  Admission No: {student.admissionNumber}
                </Typography>
              </Grid>

              <Grid
                item
                xs={12}
                md={3}
                sx={{
                  display: "flex",
                  justifyContent: { xs: "flex-start", sm: "flex-end" },
                }}
              >
                <Chip
                  label={student.status}
                  color={student.status === "Active" ? "success" : "default"}
                  icon={
                    student.status === "Active" ? <CheckCircleIcon /> : null
                  }
                  sx={{
                    fontSize: "0.9rem",
                    fontWeight: 600,
                    height: 32,
                    px: 2,
                  }}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <CalendarIcon sx={{ mr: 2, color: "primary.main" }} />
                  <Typography variant="body1">
                    <strong>Date of Birth:</strong> {formatDate(student.dob)} (
                    {calculateAge(student.dob)} years)
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <CalendarIcon sx={{ mr: 2, color: "primary.main" }} />
                  <Typography variant="body1">
                    <strong>Joined:</strong> {formatDate(student.joiningDate)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs Section - Enhanced with more tabs */}
      <Paper sx={{ borderRadius: "16px", mb: 4 }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            px: 3,
            pt: 2,
          }}
        >
          <Tab label="Overview" />
          <Tab label="Parents & Family" />
          <Tab label="Academic Info" />
          <Tab label="Medical Details" />
          <Tab label="Emergency Contacts" />
          <Tab label="Documents" />
          <Tab label="Siblings" />
          <Tab label="Previous School" />
        </Tabs>

        <Box sx={{ p: 4 }}>
          {/* Tab 1: Overview */}
          {tabValue === 0 && (
            <Grid container spacing={4}>
              <Grid item xs={12} md={8}>
                <Card sx={{ borderRadius: "12px", mb: 3, minHeight: 375 }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight="600" gutterBottom>
                      Personal Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Full Name
                        </Typography>
                        <Typography variant="body1" sx={{textTransform: "uppercase"}}>
                          {student.firstName} {student.lastName}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Gender
                        </Typography>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <GenderIcon gender={student.gender} />
                          <Typography variant="body1">
                            {student.gender}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Date of Birth
                        </Typography>
                        <Typography variant="body1">
                          {formatDate(student.dob)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Age
                        </Typography>
                        <Typography variant="body1">
                          {calculateAge(student.dob)} years
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Blood Group
                        </Typography>
                        <Typography variant="body1">
                          {student.bloodGroup || "Not specified"}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Religion
                        </Typography>
                        <Typography variant="body1">
                          {student.religion || "Not specified"}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Nationality
                        </Typography>
                        <Typography variant="body1">
                          {student.nationality || "Not specified"}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Mother Tongue
                        </Typography>
                        <Typography variant="body1">
                          {student.motherTongue || "Not specified"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                <Card sx={{ borderRadius: "12px", minHeight: 200 }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight="600" gutterBottom>
                      Address Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Place of Birth
                        </Typography>
                        <Typography variant="body1">
                          {student.placeOfBirth || "Not specified"}
                        </Typography>
                      </Grid>
                      {/* <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Place of Origin
                        </Typography>
                        <Typography variant="body1">
                          {student.placeOfOrigin || "Not specified"}
                        </Typography>
                      </Grid> */}
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Current Address
                        </Typography>
                        <Typography variant="body1">
                          {student.address || "Not specified"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card sx={{ borderRadius: "12px", mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight="600" gutterBottom>
                      Academic Information
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          <ClassIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Class"
                          secondary={student.class || "Not specified"}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <BookIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Admission Number"
                          secondary={student.admissionNumber || "Not specified"}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <CalendarIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Joining Date"
                          secondary={formatDate(student.joiningDate)}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <SchoolIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Previous School"
                          secondary={student.previousSchool || "Not specified"}
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>

                <Card sx={{ borderRadius: "12px", minHeight: 200 }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight="600" gutterBottom>
                      Student Status
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        my: 2,
                      }}
                    >
                      <Chip
                        label={student.status}
                        color={
                          student.status === "Active" ? "success" : "error"
                        }
                        icon={<CheckCircleIcon />}
                        sx={{
                          fontSize: "1rem",
                          fontWeight: 600,
                          height: 40,
                          px: 2,
                        }}
                      />
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      align="center"
                    >
                      {student.status === "Active"
                        ? "Student is currently enrolled and attending classes."
                        : "Student is not currently enrolled."}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Tab 2: Parents & Family */}
          {tabValue === 1 && (
            <Grid container spacing={3} >
              <Grid item xs={12} md={6}>
                <Card sx={{ borderRadius: "12px", mb: 3, minHeight: "300px" }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight="600" gutterBottom>
                      Parent/Guardian Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Parent Name
                        </Typography>
                        <Typography variant="body1">
                          {student.parentName}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Parent Login ID
                        </Typography>
                        <Typography variant="body1">
                          {student.username || "Not specified"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Phone
                        </Typography>
                        <Typography variant="body1">
                          {student.parentPhone}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Email
                        </Typography>
                        {!student.parentEmail ||
                        student.parentEmail.trim() === "" ||
                        student.parentEmail === "Not specified" ? (
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.5,
                              mt: 0.5,
                              px: 1,
                              py: 0.25,
                              borderRadius: "6px",
                              bgcolor: (theme) =>
                                alpha(theme.palette.error.main, 0.1),
                              color: "error.main",
                              border: (theme) =>
                                `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                            }}
                          >
                            <WarningIcon color="error" sx={{ fontSize: 16 }} />
                            <Typography
                              variant="body2"
                              fontWeight="600"
                              color="error.main"
                            >
                              Email Not Specified
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body1">
                            {student.parentEmail}
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Address
                        </Typography>
                        <Typography variant="body1">
                          {student.address}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card sx={{ borderRadius: "12px", minHeight: "300px" }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight="600" gutterBottom>
                      Mother Details
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Mother Name
                        </Typography>
                        <Typography variant="body1">
                          {student.motherName || "Not specified"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Qualification
                        </Typography>
                        <Typography variant="body1">
                          {student.motherQualification || "Not specified"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Occupation
                        </Typography>
                        <Typography variant="body1">
                          {student.motherOccupation || "Not specified"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Work Place
                        </Typography>
                        <Typography variant="body1">
                          {student.motherWorkPlace || "Not specified"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Mobile
                        </Typography>
                        <Typography variant="body1">
                          {student.motherMobile || "Not specified"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Official Address
                        </Typography>
                        <Typography variant="body1">
                          {student.motherOfficialAddress || "Not specified"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Tab 3: Academic Info */}
          {tabValue === 2 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Card sx={{ borderRadius: "12px" }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight="600" gutterBottom>
                      Academic Details
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Current Class
                        </Typography>
                        <Typography variant="h6" color="primary">
                          {student.class}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Admission Number
                        </Typography>
                        <Typography variant="body1">
                          {student.admissionNumber}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Joining Date
                        </Typography>
                        <Typography variant="body1">
                          {formatDate(student.joiningDate)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Status
                        </Typography>
                        <Chip
                          label={student.status}
                          color={
                            student.status === "Active" ? "success" : "error"
                          }
                          size="small"
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Tab 4: Medical Details */}
          {tabValue === 3 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card sx={{ borderRadius: "12px", mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight="600" gutterBottom>
                      Medical Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Allergies
                        </Typography>
                        <Typography variant="body1">
                          {student.allergies || "None reported"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Surgeries
                        </Typography>
                        <Typography variant="body1">
                          {student.surgeries || "None reported"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Chronic Illness
                        </Typography>
                        <Typography variant="body1">
                          {student.chronicIllness || "None reported"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Immunization Status
                        </Typography>
                        <Typography variant="body1">
                          {student.immunization || "Not specified"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Blood Group
                        </Typography>
                        <Typography variant="body1">
                          {student.bloodGroup || "Not specified"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Tab 5: Emergency Contacts */}
          {tabValue === 4 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card sx={{ borderRadius: "12px", mb: 3, minHeight: "200px" }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight="600" gutterBottom>
                      Emergency Contact Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Emergency Contact Name
                        </Typography>
                        <Typography variant="body1">
                          {student.emergencyContactName || student.parentName}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Emergency Phone
                        </Typography>
                        <Typography variant="h6" color="error">
                          {student.emergencyContactPhone || student.parentPhone}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Relationship
                        </Typography>
                        <Typography variant="body1">
                          {student.emergencyRelationship || "Parent"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card sx={{ borderRadius: "12px", minHeight: "200px" }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight="600" gutterBottom>
                      Guardian Details (If Applicable)
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Guardian Name
                        </Typography>
                        <Typography variant="body1">
                          {student.guardianName || "Not specified"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Relationship
                        </Typography>
                        <Typography variant="body1">
                          {student.guardianRelationship || "Not specified"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Phone
                        </Typography>
                        <Typography variant="body1">
                          {student.guardianPhone ||
                            student.guardianMobile ||
                            "Not specified"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Tab 6: Documents */}
          {tabValue === 5 && (
            <Box>
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" fontWeight="600" gutterBottom>
                  Student Documents & Files
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  View all uploaded documents, images, and files for this
                  student. Click on images to enlarge or use the download button
                  to save files.
                </Typography>
              </Box>

              {renderDocumentsGrid()}
            </Box>
          )}

          {/* Tab 7: Siblings */}
          {tabValue === 6 && (
            <Box>
              <Card sx={{ borderRadius: "12px" }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="600" gutterBottom>
                    Sibling Details
                  </Typography>
                  {renderSiblingsTable()}
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Tab 8: Previous School */}
          {tabValue === 7 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card sx={{ borderRadius: "12px" }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight="600" gutterBottom>
                      Previous School Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Attended School Previously?
                        </Typography>
                        <Typography variant="body1">
                          {student.previouslyAttendedSchool === "yes"
                            ? "Yes"
                            : "No"}
                        </Typography>
                      </Grid>
                      {student.previouslyAttendedSchool === "yes" && (
                        <>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              School Name
                            </Typography>
                            <Typography variant="body1">
                              {student.previousSchoolName || "Not specified"}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Typography variant="body2" color="text.secondary">
                              Duration
                            </Typography>
                            <Typography variant="body1">
                              {student.previousSchoolDuration ||
                                "Not specified"}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Typography variant="body2" color="text.secondary">
                              Class Attended
                            </Typography>
                            <Typography variant="body1">
                              {student.previousSchoolClass || "Not specified"}
                            </Typography>
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </Box>
      </Paper>

      {/* Edit Student Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleEditClose}
        maxWidth="lg"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle>
          Edit Student: {student.firstName} {student.lastName}
        </DialogTitle>
        <DialogContent dividers>
          {fullStudentData && (
            <AddStudentForm
              mode="edit"
              initialData={fullStudentData}
              onSubmit={async (payload, setServerError) => {
                try {
                  const token = localStorage.getItem("token");
                  const response = await fetch(
                    `${apiEndpoints.student}?guid=${guid}`,
                    {
                      method: "PUT",
                      headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                      },
                      body: JSON.stringify(payload),
                    },
                  );

                  const data = await response.json();
                  if (data.status === "success") {
                    handleEditSuccess();
                  } else {
                    setServerError(data.message || "Failed to update student");
                  }
                } catch (err) {
                  console.error("Error updating student:", err);
                  setServerError("Error updating student");
                }
              }}
              onClose={handleEditClose}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose} startIcon={<CancelIcon />}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {student.firstName}{" "}
            {student.lastName}? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* File Modal */}
      {renderFileModal()}
    </Container>
  );
};

export default StudentDetail;
